import { RootState } from '@Common/models/redux/store';
import { Button, Grid, Theme, Tooltip } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { extensionLinks } from 'config/constants';
import Track from 'src/authentication/handlers/track';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getQueryParams } from '@Common/handlers/helper';
import logger from 'core/logger/logger';

const useStyles = makeStyles<Theme, IPprops>((theme) => ({
  btnContainer: {
    border: (props) =>
      props.variant === 'solid' ? 'none' : `1px solid ${theme.palette.primary.main}`,
    backgroundColor: (props) =>
      props.variant === 'solid' ? theme.palette.primary.main : theme.palette.background.paper,
    color: theme.palette.primary.main,
    borderRadius: '100px',
    '&:hover': {
      backgroundColor: (props) =>
        props.variant === 'solid'
          ? theme.palette.primary.dark
          : `${props.variant === 'outlined' ? '' : `${theme.palette.primary.main}0a`}`,
    },
    [theme.breakpoints.down(1250)]: {
      display: 'none',
    },
  },
  addToChromeBtn: {
    padding: '6px 16px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: (props) =>
      props.variant === 'solid' ? theme.palette.primary.contrastText : theme.palette.primary.main,
  },
  extTooltip: {
    fontSize: 12,
    padding: 6,
    maxWidth: 140,
    textAlign: 'center',
  },
}));
interface IPprops {
  originComp: string;
  onAddClick?: Function;
  text?: string;
  variant?: string;
}

function AddToChrome({ originComp, onAddClick, text, variant }: IPprops) {
  const classes = useStyles({ originComp, variant });
  const [showAddChromeBtn, setShowAddChromeBtn] = useState(true);
  const { isChrome, isEdge, isChromeExtInstalled } = useSelector(
    (state: RootState) => state.common.platform
  );
  const isFirebaseInitiated = useSelector(
    (rootState: RootState) => rootState.common?.isFirebaseInitiated
  );
  const [showExtensionButtonFirbase, setShowExtensionButtonFirbase] = useState(false);

  /*
    We receive the state from the firebase using originComp (from props)
    and show it to the users accordingly.
    To hide/show the UpsellButton,
    there must be a value in firebase config (extensionUpsellConfig) with the same name as originComp.
  */
  const extensionButtonFirebaseListener = () => {
    if (window.firebase && window.firebase?.apps?.length) {
      window.firebase
        .database()
        ?.ref('extensionUpsellConfig')
        .on('value', (snapshot: any) => {
          try {
            const data = snapshot.val();
            const extensionUpsellConfig = { ...data };
            setShowExtensionButtonFirbase(extensionUpsellConfig[originComp]);
          } catch (e) {
            logger('FirebaseExtensionUpsellConfig', e);
          }
        });
    }
  };

  useEffect(() => {
    extensionButtonFirebaseListener();
  }, [isFirebaseInitiated]);

  const onClickAddToChromeBtn = () => {
    setShowAddChromeBtn(false);
    Track.extensionLinkClicked('chrome', originComp);
    window.open(
      `${extensionLinks.chrome}${getQueryParams(originComp)}&utm_content=banner_01`,
      '_blank',
      'left=120,top=150,height=480,width=1200'
    );

    if (onAddClick) onAddClick();
  };

  if ((!isChrome && !isEdge) || isChromeExtInstalled || !showAddChromeBtn) {
    return null;
  }

  return showExtensionButtonFirbase ? (
    <Grid item className={classes.btnContainer}>
      <Tooltip
        arrow
        title="Take QuillBot everywhere you write"
        placement="bottom"
        classes={{
          tooltip: classes.extTooltip,
        }}
      >
        <Button onClick={onClickAddToChromeBtn} className={classes.addToChromeBtn}>
          <img
            loading="lazy"
            src="/images/theme/commonImages/chrome.png"
            alt="chrome icon"
            height="22px"
            width="22px"
            style={{ marginRight: '7px' }}
          />
          {text || 'Add to Chrome. It’s free!'}
        </Button>
      </Tooltip>
    </Grid>
  ) : null;
}

export default AddToChrome;
